import type { LayerDefinition } from '@susu/custom-made/utils/preview/layers';
import type { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { checkConfigurationTypeRotationAvailable } from '@susu/custom-made/utils/utils';
import type { SyntheticEvent } from 'react';
import React from 'react';

import { getPreviewLayerClasses } from './previewLayer.classes';
import PreviewRotationLayer from './previewRotationLayer';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';

const { SHOW_3D_PREVIEW_IMAGE } = FEATURE_TOGGLES;

const PreviewOtherLayers = React.memo(
  ({
    layers, // eslint-disable-line react/prop-types
    configurationType, // eslint-disable-line react/prop-types
    setLayersDone, // eslint-disable-line react/prop-types
  }: Readonly<{
    layers: LayerDefinition[];
    configurationType?: ConfigurationTypes;
    setLayersDone?: React.Dispatch<React.SetStateAction<string[]>> | undefined;
  }>) => {
    const show3DPreviewImageFeatureEnabled = useFeatureIsOn(SHOW_3D_PREVIEW_IMAGE);
    const onLayerFailed = (e: SyntheticEvent<HTMLImageElement, Event>) => {
      if (setLayersDone) {
        setLayersDone(prev => [...prev, 'failed']);
      }
      e.currentTarget.style.display = 'none';
    };

    const onLayerLoad = (e: SyntheticEvent<HTMLImageElement, Event>) => {
      if (setLayersDone) {
        setLayersDone(prev => [...prev, 'done']);
      }
      e.currentTarget.style.display = 'block';
    };

    return [...layers].splice(1).map((layer, index) => (
      <React.Fragment key={`otherlayerContainer_${index}`}>
        {checkConfigurationTypeRotationAvailable(
          configurationType,
          show3DPreviewImageFeatureEnabled,
        ) && (
          <PreviewRotationLayer
            layer={layer}
            index={index}
            keyPrefix="otherlayer"
            onLayerFailed={onLayerFailed}
            onLayerLoaded={onLayerLoad}
          />
        )}
        {!checkConfigurationTypeRotationAvailable(
          configurationType,
          show3DPreviewImageFeatureEnabled,
        ) && (
          <div className={getPreviewLayerClasses(true)}>
            <img
              src={layer.src}
              srcSet={layer.srcset}
              alt={`otherlayer_${index}`}
              onError={onLayerFailed}
              onLoad={onLayerLoad}
              sizes={layer.sizes}
              fetchPriority="high"
              loading="eager"
            />
          </div>
        )}
      </React.Fragment>
    ));
  },
);

PreviewOtherLayers.displayName = 'PreviewOtherLayers';

export default PreviewOtherLayers;
